import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  contentModerationService,
  ContentFlag,
  FlagStats,
} from '../../services/contentModerationService';
import { useUser } from '../../contexts/AuthContext';
import ContentReviewCard from '../../components/admin/ContentReviewCard';

/**
 * ContentModeration Component
 * Page for reviewing and moderating flagged content
 *
 * Features:
 * - Display all flagged content
 * - Filter by content type and status
 * - Review and take action on flags
 * - Statistics dashboard
 * - Bulk moderation actions
 */
const ContentModeration: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useUser();

  // State
  const [flags, setFlags] = useState<ContentFlag[]>([]);
  const [filteredFlags, setFilteredFlags] = useState<ContentFlag[]>([]);
  const [stats, setStats] = useState<FlagStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingFlagId, setProcessingFlagId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('all');
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    flagId: string;
    action: 'approve' | 'reject';
  } | null>(null);
  const [actionType, setActionType] = useState<
    'warning' | 'content_hidden' | 'content_removed' | 'user_suspended'
  >('content_hidden');
  const [reviewNotes, setReviewNotes] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  /**
   * Load flags on mount
   */
  useEffect(() => {
    loadFlags();
    loadStats();
  }, []);

  /**
   * Apply filters when they change
   */
  useEffect(() => {
    applyFilters();
  }, [flags, activeTab, contentTypeFilter]);

  /**
   * Load all flags
   */
  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);

      const loadedFlags = await contentModerationService.getFlags();
      setFlags(loadedFlags);
    } catch (err: any) {
      console.error('Error loading flags:', err);
      setError(err.message || 'Failed to load flagged content');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load flag statistics
   */
  const loadStats = async () => {
    try {
      const flagStats = await contentModerationService.getFlagStats();
      setStats(flagStats);
    } catch (err: any) {
      console.error('Error loading stats:', err);
    }
  };

  /**
   * Apply filters to flags
   */
  const applyFilters = () => {
    let filtered = [...flags];

    // Tab filter (all, pending, approved, rejected)
    if (activeTab === 1) {
      filtered = filtered.filter((flag) => flag.status === 'pending');
    } else if (activeTab === 2) {
      filtered = filtered.filter((flag) => flag.status === 'approved');
    } else if (activeTab === 3) {
      filtered = filtered.filter((flag) => flag.status === 'rejected');
    }

    // Content type filter
    if (contentTypeFilter !== 'all') {
      filtered = filtered.filter((flag) => flag.contentType === contentTypeFilter);
    }

    setFilteredFlags(filtered);
  };

  /**
   * Handle approve flag - open dialog
   */
  const handleApproveClick = (flagId: string) => {
    setActionDialog({
      open: true,
      flagId,
      action: 'approve',
    });
    setActionType('content_hidden');
    setReviewNotes('');
  };

  /**
   * Handle reject flag - open dialog
   */
  const handleRejectClick = (flagId: string) => {
    setActionDialog({
      open: true,
      flagId,
      action: 'reject',
    });
    setReviewNotes('');
  };

  /**
   * Handle action confirm
   */
  const handleActionConfirm = async () => {
    if (!actionDialog || !currentUser?.uid) return;

    try {
      setProcessingFlagId(actionDialog.flagId);

      if (actionDialog.action === 'approve') {
        await contentModerationService.approveFlag(
          actionDialog.flagId,
          currentUser.uid,
          actionType,
          reviewNotes || undefined
        );
        showSnackbar('Content flag approved and action taken', 'success');
      } else {
        await contentModerationService.rejectFlag(
          actionDialog.flagId,
          currentUser.uid,
          reviewNotes || undefined
        );
        showSnackbar('Content flag rejected - no action taken', 'success');
      }

      setActionDialog(null);
      setReviewNotes('');

      // Reload flags
      await Promise.all([loadFlags(), loadStats()]);
    } catch (err: any) {
      console.error('Error processing flag:', err);
      showSnackbar(err.message || 'Failed to process flag', 'error');
    } finally {
      setProcessingFlagId(null);
    }
  };

  /**
   * Handle view content
   */
  const handleViewContent = (contentType: string, contentId: string) => {
    // Navigate based on content type
    switch (contentType) {
      case 'person':
        navigate(`/people/${contentId}`);
        break;
      case 'photo':
        showSnackbar('Photo view not yet implemented', 'info');
        break;
      case 'comment':
        showSnackbar('Comment view not yet implemented', 'info');
        break;
      case 'relationship':
        showSnackbar('Relationship view not yet implemented', 'info');
        break;
    }
  };

  /**
   * Show snackbar message
   */
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Close snackbar
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Close action dialog
   */
  const handleCloseActionDialog = () => {
    setActionDialog(null);
    setReviewNotes('');
  };

  /**
   * Render loading state
   */
  if (loading && flags.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        {/* Page Header */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                Content Moderation
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Review and moderate flagged content
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadFlags();
                loadStats();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {/* Statistics */}
          {stats && (
            <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Chip
                  label={`${stats.pending} Pending`}
                  color="warning"
                  sx={{ fontWeight: 600 }}
                />
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  {stats.byContentType.person} persons
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stats.byContentType.photo} photos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stats.byContentType.comment} comments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stats.byContentType.relationship} relationships
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ mb: 3 }}>
          <Box p={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Content Type</InputLabel>
                  <Select
                    value={contentTypeFilter}
                    onChange={(e) => setContentTypeFilter(e.target.value)}
                    label="Content Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="person">Person</MenuItem>
                    <MenuItem value="photo">Photo</MenuItem>
                    <MenuItem value="comment">Comment</MenuItem>
                    <MenuItem value="relationship">Relationship</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
            <Tab label={`All Flags (${flags.length})`} />
            <Tab
              label={`Pending (${flags.filter((f) => f.status === 'pending').length})`}
            />
            <Tab
              label={`Reviewed (${
                flags.filter((f) => f.status === 'approved' || f.status === 'rejected').length
              })`}
            />
          </Tabs>
        </Paper>

        {/* Empty State */}
        {filteredFlags.length === 0 && !loading ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No Flagged Content
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {activeTab === 1
                ? 'All flags have been reviewed. Great job!'
                : 'No content flags match your current filters.'}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin')}
            >
              Back to Dashboard
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredFlags.map((flag) => (
              <Grid item xs={12} sm={6} md={4} key={flag.id}>
                <ContentReviewCard
                  flag={flag}
                  onApprove={handleApproveClick}
                  onReject={handleRejectClick}
                  onViewContent={handleViewContent}
                  loading={processingFlagId === flag.id}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Action Dialog */}
        <Dialog
          open={actionDialog?.open || false}
          onClose={handleCloseActionDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {actionDialog?.action === 'approve' ? 'Approve Flag & Take Action' : 'Reject Flag'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {actionDialog?.action === 'approve'
                ? 'The content will be actioned based on your selection below.'
                : 'The content will remain visible and no action will be taken.'}
            </DialogContentText>

            {actionDialog?.action === 'approve' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  value={actionType}
                  onChange={(e) =>
                    setActionType(
                      e.target.value as
                        | 'warning'
                        | 'content_hidden'
                        | 'content_removed'
                        | 'user_suspended'
                    )
                  }
                  label="Action"
                >
                  <MenuItem value="warning">Send Warning to User</MenuItem>
                  <MenuItem value="content_hidden">Hide Content</MenuItem>
                  <MenuItem value="content_removed">Remove Content</MenuItem>
                  <MenuItem value="user_suspended">Suspend User</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Review Notes (Optional)"
              placeholder="Add notes about your decision..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              helperText="These notes will be logged in the audit trail"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseActionDialog}>Cancel</Button>
            <Button
              onClick={handleActionConfirm}
              color={actionDialog?.action === 'approve' ? 'error' : 'success'}
              variant="contained"
              disabled={processingFlagId !== null}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ContentModeration;
